import { cva } from 'class-variance-authority';

/**
 * Text input class recipes.
 *
 * Like `Button.recipe`, these contain ZERO `dark:` variants — colour comes entirely from the
 * `--ds-input-*` slots that `.ds-input` / `.ds-input-pill` set per theme in
 * `components/styles/design-system.css`.
 *
 * Every state selector reads Base UI's `Field.Root` attributes through the `group/ti` marker
 * (`data-focused`, `data-filled`, `data-invalid`, `data-disabled`), so there is no React state
 * behind the shrink or the colour changes — which also means the states are reachable from a
 * story or a test by rendering, not by simulating.
 *
 * The state selectors are written to be MUTUALLY EXCLUSIVE rather than relying on utility sort
 * order, the same discipline the button recipe uses. Precedence, highest first:
 *
 *   disabled → invalid → focused → hover → resting
 *
 * An errored field therefore stays red while focused, which is what the design's separate Error
 * and Focus cells imply.
 *
 * ── Where the padding lives ────────────────────────────────────────────────────────────────────
 *
 * All of it is on the CONTROL, none on the box. That is the one structural decision to understand
 * before changing anything here.
 *
 * The obvious build — pad the box, put a 20px-tall input on the value line — leaves the `<input>`
 * a thin strip across the middle of a 60px field, so most of what looks like a text box is dead to
 * the pointer and clicking near the edges does nothing. Paying the padding from inside the input
 * instead makes the element fill the whole box, so every pixel of the field is a click target and
 * the caret lands where it should. It is what MUI's outlined input does, for the same reason.
 */

/**
 * Marker + layout for `Field.Root`.
 *
 * The `.ds-input` slot class lives HERE rather than on the bordered box, even though the box is what
 * consumes most of the slots. Custom properties cascade, so everything inside inherits them — and
 * anything rendered as a SIBLING of the box (the pill's helper text) would otherwise fall outside
 * their scope and silently resolve to inherited colour.
 */
export const textInputRootRecipe = cva(['group/ti ds-input', 'relative flex w-full flex-col']);

/**
 * The bordered box.
 *
 * Holds no padding of its own (see the note above) — it is a border, a fill and a radius. The only
 * exception is the bottom inset when helper text is present, which the control can no longer pay for
 * because the helper sits below it.
 *
 * `gap-[4px]` separates the value block from the helper text. Absolutely positioned children (the
 * label and the icons) are out of flow, so the gap only ever applies between the control and the
 * helper row — a field without helper text gets no stray space.
 *
 * `min-h-[60px]` matches the design's resting height, which the control's own box already produces;
 * it is a floor rather than the mechanism, so a taller caller-supplied line height cannot clip.
 *
 * ── Why the stroke is drawn with shadows, not the border ───────────────────────────────────────
 *
 * `border-width` NEVER changes here, in any state. The design thickens the stroke to 1.5px on focus,
 * error and disabled, and the obvious build is to grow the border — but a border is layout. Widening
 * it moves the padding box, which shifts the absolutely positioned label and icons AND the value
 * text by half a pixel, so the field's contents visibly jump on every focus and every tab through a
 * group.
 *
 * So the border stays at its resting 1px and everything state-dependent is painted as an inset
 * `box-shadow`, which occupies no layout space at all:
 *
 *   inset 0 0 0 0.5px  --ds-input-stroke-extra   the half-pixel that makes the stroke 1.5px
 *   inset 0 0 0 2.5px  --ds-input-ring-current   the lighter ring, in the 2px inside that
 *
 * Earlier shadows paint over later ones, so reading inward that is 1px of border, 0.5px of the same
 * colour, then 2px of ring. Both slots default to `transparent`, which is what lets the shadow rule
 * itself be unconditional — only the slots are switched per state, by the same mutually exclusive
 * selectors that set the border colour.
 */
export const textInputBoxRecipe = cva(
  [
    'relative flex w-full flex-col gap-[4px]',
    'min-h-[60px]',
    'border-solid border-[length:var(--ds-input-border-width)] border-[var(--ds-input-border)]',
    'bg-[var(--ds-input-bg)]',
    'cursor-text',
    'transition-[border-color,background-color,box-shadow] duration-150 motion-reduce:transition-none',

    // Hover is excluded from every other state so it can never fight them.
    'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:border-[var(--ds-input-border-hover)]',

    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-input-border-focus)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-input-border-invalid)]',
    'group-data-[disabled]/ti:border-[var(--ds-input-border-disabled)]',
    'group-data-[disabled]/ti:bg-[var(--ds-input-bg-disabled)]',
    'group-data-[disabled]/ti:cursor-not-allowed',

    // Both shadow layers off by default, so the rule below can be unconditional.
    '[--ds-input-stroke-extra:transparent] [--ds-input-ring-current:transparent]',
    'shadow-[inset_0_0_0_0.5px_var(--ds-input-stroke-extra),inset_0_0_0_2.5px_var(--ds-input-ring-current)]',

    // The half-pixel thickening, in the same colour as whichever border is showing. Focus, error and
    // disabled each reach 1.5px; hover and resting stay at 1px.
    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:[--ds-input-stroke-extra:var(--ds-input-border-focus)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:[--ds-input-stroke-extra:var(--ds-input-border-invalid)]',
    'group-data-[disabled]/ti:[--ds-input-stroke-extra:var(--ds-input-border-disabled)]',

    /**
     * The ring, drawn only while focused — inside the border, flush against it.
     *
     * An errored field keeps its red border when focused, so a blue ring inside it would read as a
     * bug. Remapping the slot rather than adding a focused-and-invalid rule means the line below
     * stays a single read, exactly as the border rules do.
     *
     * The ring alone is a low-contrast tint and would not satisfy WCAG 1.4.11; the indicator that
     * carries the state is the 1.5px border.
     */
    'group-data-[invalid]/ti:[--ds-input-ring:var(--ds-input-ring-invalid)]',
    'group-data-[focused]/ti:[--ds-input-ring-current:var(--ds-input-ring)]',
  ],
  {
    variants: {
      /**
       * Figma's `Corner Radius` axis. The design draws three of these four; `middle` has no cell
       * and is extrapolated — see `TextInputPosition`.
       */
      position: {
        standalone: 'rounded-[16px]',
        first: 'rounded-t-[16px] rounded-b-none',
        middle: 'rounded-none',
        last: 'rounded-b-[16px] rounded-t-none',
      },
      /**
       * The bottom inset, which moves between the box and the control depending on whether anything
       * is rendered below the value.
       *
       * A plain 10px: because the border no longer changes width, nothing here has to compensate for
       * it, and the box measures exactly 60px in every state (1 + 28 + 20 + 10 + 1).
       */
      hasHelper: {
        true: 'pb-[10px]',
        false: 'pb-0',
      },
    },
    defaultVariants: { position: 'standalone', hasHelper: false },
  }
);

/**
 * The floating label.
 *
 * Resting it is centred in the 60px box; shrunk it rises to 11px near the top. It shrinks when the
 * field is filled OR focused — so a focused empty field shows the shrunk label above an empty line,
 * ready to type.
 *
 * This is a deliberate divergence from the file: Figma's Focus/Empty cell keeps the label at 14px on
 * the value line, which would leave the caret sitting underneath the label text. Agreed with the
 * requester that the label shrinks on focus.
 *
 * Deliberately NOT `pointer-events-none`, which is the reflex for an overlaid float label. This is a
 * real `<label>` wired to the control by `Field`, so a click on it focuses the input natively;
 * making it click-through would instead drop clicks on the part of it that overhangs the input.
 */
export const textInputLabelRecipe = cva(
  [
    'absolute top-0 left-[24px] origin-left cursor-text select-none',
    'font-sans text-[14px] leading-5 text-[var(--ds-input-label)]',
    'max-w-[calc(100%_-_48px)] truncate',
    // 19px from the padding edge centres the 20px line box in the 60px field; 10px puts it on the
    // shrunk line. The design places the resting label at the box's centre rather than on the value
    // line, so the two are 9px apart.
    'translate-y-[19px] scale-100',
    /**
     * Rise and shrink are ONE composited animation, and both halves of that are deliberate.
     *
     * The properties named here are `translate` and `scale`, not `transform`. Tailwind v4's
     * `translate-*` and `scale-*` utilities set the standalone CSS properties of those names, so a
     * transition on `transform` matches nothing at all — which is what made the label jump to its
     * shrunk position instantly and only then animate its size.
     *
     * The size change is a `scale` rather than a `font-size` for the same reason it is on the same
     * property as the movement: `font-size` relayouts the text on every frame on the main thread,
     * while `translate`/`scale` are composited, so the two could never stay in step. Scaling also
     * fixes the truncation point, which under a `font-size` animation moved mid-flight and made the
     * ellipsis jitter on a long label.
     *
     * `scale-100` is on the base rather than left implicit: interpolating from `scale: none` is not
     * reliably animatable, so the resting state has to say 1 out loud.
     */
    'transition-[translate,scale,color] duration-150 motion-reduce:transition-none',

    // 11/14 — the design's shrunk size expressed as a ratio of the resting one, since the font-size
    // itself no longer changes.
    'group-data-[filled]/ti:translate-y-[10px] group-data-[filled]/ti:scale-[0.785714]',
    'group-data-[focused]/ti:translate-y-[10px] group-data-[focused]/ti:scale-[0.785714]',
    'group-data-[disabled]/ti:cursor-not-allowed group-data-[disabled]/ti:text-[var(--ds-input-label-disabled)]',
  ],
  {
    variants: {
      /**
       * A leading icon shares the value line, so the resting label has to clear it (16px icon plus
       * the 10px gap). Once shrunk the label is on its own line above the icon and the offset is
       * dropped, which reads as the label sliding up and left together.
       *
       * A `translate-x` rather than the `padding-left` this used to be, so the horizontal move joins
       * the same single composited animation instead of relayouting alongside it. The narrower
       * `max-w` is the cost: a translate does not shrink the content box the way padding did, so the
       * clamp has to account for the offset itself or a long label would overrun the right gutter.
       * It is set for both states, not animated, since it only governs where truncation falls.
       */
      hasLeftIcon: {
        true: 'translate-x-[26px] max-w-[calc(100%_-_74px)] group-data-[filled]/ti:translate-x-0 group-data-[focused]/ti:translate-x-0',
        false: '',
      },
    },
    defaultVariants: { hasLeftIcon: false },
  }
);

/**
 * Shared reset for both shapes.
 *
 * Preflight is deliberately absent from the token layer, so the browser's own input styling is still
 * in play and every reset the field needs is spelled out rather than assumed.
 */
const controlBase = [
  'm-0 block w-full appearance-none border-0 bg-transparent',
  'font-sans leading-5 text-[var(--ds-input-value)]',
  'outline-none',
  'placeholder:text-[var(--ds-input-placeholder)]',
  'disabled:cursor-not-allowed disabled:text-[var(--ds-input-value-disabled)]',
  // Edge and Chrome add their own reveal/clear affordances to password and search inputs, which
  // would sit beside a caller's `rightIcon`.
  '[&::-ms-reveal]:hidden [&::-ms-clear]:hidden',
  '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden',
];

/**
 * The control for the 60px variant, which is also the field's entire hit area.
 *
 * `pt-[28px]` puts the value's 20px line box 29px from the top of the field, which is where the
 * design draws it (node 447:35145) — and, because the padding is inside the input, everything above
 * that line is still part of the input rather than dead space on the box.
 *
 * Both text sizes in this component are BELOW the 16px at which iOS Safari stops zooming the
 * viewport on focus, so a tap will zoom the page on an iPhone. Reproduced as specified (Body S Reg
 * 14) rather than silently bumped — flagged on HNS-322 for design.
 */
export const textInputControlRecipe = cva([...controlBase, 'text-[14px]', 'pt-[28px]', 'cursor-text'], {
  variants: {
    /** When helper text follows, the bottom inset moves to the box — see `textInputBoxRecipe`. */
    hasHelper: {
      true: 'pb-0',
      false: 'pb-[10px]',
    },
    /** 24px gutter, plus 16px of icon and a 10px gap when a slot is filled. */
    hasLeftIcon: { true: 'pl-[50px]', false: 'pl-[24px]' },
    hasRightIcon: { true: 'pr-[50px]', false: 'pr-[24px]' },
  },
  defaultVariants: { hasHelper: false, hasLeftIcon: false, hasRightIcon: false },
});

/**
 * An icon slot on the 60px variant.
 *
 * Positioned absolutely so the control underneath can span the full box. The two sides sit at
 * DIFFERENT heights, which is authored rather than accidental: the leading icon is centred on the
 * value line (node 447:35144) while the trailing one is centred in the field.
 *
 * These stay pointer-interactive, since the trailing slot's main job is a control such as a
 * reveal-password toggle. The cost is that a purely decorative icon swallows a 16px click instead of
 * focusing the field.
 */
export const textInputAffixRecipe = cva(
  [
    'absolute inline-flex size-[16px] shrink-0 items-center justify-center',
    'text-[var(--ds-input-label)]',
    'group-data-[disabled]/ti:text-[var(--ds-input-label-disabled)]',
    '[&_svg]:size-full',
  ],
  {
    variants: {
      side: {
        left: 'left-[24px] top-[30px]',
        right: 'right-[24px] top-[21px]',
      },
    },
    defaultVariants: { side: 'right' },
  }
);

/** Helper / error text. Inside the border for this variant, below it for the pill. */
export const textInputHelperRecipe = cva(
  [
    'flex items-center',
    // `m-0` is load-bearing, not tidiness: `Field.Description` renders a `<p>`, and with Preflight
    // deliberately absent from the token layer it keeps the browser's default `margin: 1em 0` — 28px
    // that grew the box past 100px while the `<div>` that `Field.Error` renders stayed correct.
    'm-0',
    'font-sans leading-5 text-[var(--ds-input-helper)]',
    'group-data-[invalid]/ti:text-[var(--ds-input-helper-invalid)]',
  ],
  {
    variants: {
      shape: {
        // Aligned with the value text, which pays its own 24px gutter from inside the input. 13px
        // (Body XS) rather than the 14px the design draws, per the requester.
        default: 'px-[24px] text-[13px]',
        /**
         * Indented to the leftmost thing in the pill (node 558:23435). One value covers both cases
         * the requester asked about: a leading icon and a bare placeholder start at the SAME x,
         * because the icon is first in flow and the control follows it — so aligning to the box's
         * content edge aligns to whichever is showing.
         *
         * Figma draws this as a flat 20px because its stroke is inside-aligned and does not displace
         * children. In CSS the border does, so the border width is added back — otherwise the helper
         * sits a pixel left of the content it is meant to line up with.
         */
        pill: 'pl-[calc(20px_+_var(--ds-input-border-width))] text-[13px]',
      },
    },
    defaultVariants: { shape: 'default' },
  }
);

// ── Pill ──────────────────────────────────────────────────────────────────────────────────────

/**
 * The pill wrapper. Unlike the 60px input, the pill's helper text sits OUTSIDE the border, 4px
 * below it (node 487:38327), so the wrapper — not the bordered box — is what stacks.
 */
export const textInputPillRootRecipe = cva(['group/ti ds-input-pill', 'relative flex w-full flex-col gap-[4px]']);

/**
 * The pill box. Always fully rounded: the pill set has no Corner Radius axis, so there is no
 * `position` variant here.
 *
 * `rounded-[42px]` is authored as a fixed radius equal to the height rather than `rounded-full`, and
 * it is reproduced that way — the two only agree while the pill is exactly 42px tall.
 *
 * Horizontal padding only. The vertical inset is paid by the control, for the hit-area reason in the
 * file header.
 */
export const textInputPillBoxRecipe = cva([
  'relative flex w-full items-center gap-[4px] overflow-hidden',
  'px-[20px] rounded-[42px]',
  'border-solid border-[length:var(--ds-input-border-width)] border-[var(--ds-input-border)]',
  'bg-[var(--ds-input-bg)]',
  'cursor-text',
  'transition-[border-color,background-color,outline-color] duration-150 motion-reduce:transition-none',

  'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:border-[var(--ds-input-border-hover)]',
  'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-input-border-focus)]',
  'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-input-border-invalid)]',
  'group-data-[disabled]/ti:border-[var(--ds-input-border-disabled)]',
  'group-data-[disabled]/ti:bg-[var(--ds-input-bg-disabled)]',
  'group-data-[disabled]/ti:cursor-not-allowed',

  // As on the 60px input: an errored pill keeps its red border while focused, so the halo follows
  // the error colour rather than ringing red in blue.
  'group-data-[invalid]/ti:[--ds-input-ring:var(--ds-input-ring-invalid)]',

  // The pill's halo is drawn coincident with the box (node 487:38417), so this takes no offset —
  // which is also what the 60px input needs, since any offset opens a gap of page background
  // between the border and the halo.
  'outline-transparent group-data-[focused]/ti:outline-2 group-data-[focused]/ti:outline-offset-0',
  'group-data-[focused]/ti:outline-[var(--ds-input-ring)]',
]);

/**
 * The pill's control. `py-[10px]` over a 20px line is the pill's whole 40px interior, so the input
 * fills it top to bottom and the 42px pill is clickable throughout rather than across a 20px strip.
 */
export const textInputPillControlRecipe = cva([...controlBase, 'min-w-0 flex-1 py-[10px] text-[13px]', 'cursor-text']);

/** An icon slot on the pill, which keeps them in flow either side of the control. */
export const textInputIconRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  'text-[var(--ds-input-label)]',
  'group-data-[disabled]/ti:text-[var(--ds-input-label-disabled)]',
  '[&_svg]:size-full',
]);
